import find from 'lodash/find';
import remove from 'lodash/remove';
import filter from 'lodash/filter';
import some from 'lodash/some';
import orderBy from 'lodash/orderBy';
import slice from 'lodash/slice';
import unionBy from 'lodash/unionBy';

import { Mapper } from 'modules/core';
import {
  createOrUpdateAudition as createOrUpdateProducerAudition,
  loadAuditions as loadProducerAuditions,
  loadAudition as loadProducerAudition,
  removeAudition,
  loadAllAuditions as loadAllProducerAuditions,
} from 'modules/auditions-producers';
import { getApplications } from 'modules/audition-applications';
import { loadSavedAuditions } from 'modules/auditions-talents';

import { search, fetchAudition } from './api';
import { Audition } from './services';
import { resolveErrorMessage } from '../errors/services';
import {
  createAsyncThunk,
  createSlice,
  createSelector,
} from '@reduxjs/toolkit';

export const loadAudition = createAsyncThunk(
  'auditions/load',
  async auditionId => await fetchAudition(auditionId),
);

export const searchAuditions = createAsyncThunk(
  'auditions/search',
  async searchOptions => await search(searchOptions),
);

const auditionsSlice = createSlice({
  name: 'auditions',
  initialState: {
    auditions: [],
    loading: false,
    error: false,
    auditionsFilterData: null,
  },
  reducers: {
    setAuditionsFilterData(state, action) {
      state.auditionsFilterData = { ...action.payload };
    },
    clearAuditionsFilterData(state) {
      state.auditionsFilterData = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadAudition.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(loadAudition.fulfilled, (state, action) => {
        const newAudition = action.payload.data;
        state.loading = false;
        state.auditions = Mapper.addOrReplace(state.auditions, newAudition);
      })
      .addCase(loadAudition.rejected, (state, action) => {
        state.error = resolveErrorMessage(action.error);
        state.loading = false;
      })
      .addCase(searchAuditions.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(searchAuditions.fulfilled, (state, action) => {
        const newSearchResults = action.payload.data;
        state.loading = false;
        state.auditions = unionBy(newSearchResults, state.auditions, 'id');
      })
      .addCase(searchAuditions.rejected, (state, _) => {
        state.loading = false;
      })
      .addCase(loadProducerAudition.fulfilled, (state, action) => {
        const newAudition = action.payload.data;
        state.auditions = Mapper.addOrReplace(state.auditions, newAudition);
      })
      .addCase(createOrUpdateProducerAudition.fulfilled, (state, action) => {
        const newAudition = action.payload.data;
        state.auditions = Mapper.addOrReplace(state.auditions, newAudition);
        state.error = false;
      })
      .addCase(loadProducerAuditions.fulfilled, (state, action) => {
        const loadedAuditions = action.payload.data;
        state.auditions = unionBy(loadedAuditions, state.auditions, 'id');
      })
      .addCase(loadAllProducerAuditions.fulfilled, (state, action) => {
        const loadedAuditions = action.payload.data;
        state.auditions = unionBy(loadedAuditions, state.auditions, 'id');
      })
      .addCase(removeAudition.fulfilled, (state, action) => {
        const deletedAuditionId = action.meta.arg.auditionId;
        remove(state.auditions, audition => audition.id === deletedAuditionId);
      })
      .addCase(loadSavedAuditions.fulfilled, (state, action) => {
        const loadedAuditions = action.payload.data;
        state.auditions = unionBy(loadedAuditions, state.auditions, 'id');
      });
  },
  selectors: {
    getAuditionLoading: state => state.loading,
    getAuditions: state => state.auditions,
    getAuditionsFilterData: state => state.auditionsFilterData,
  },
});

export const reducer = auditionsSlice.reducer;
export const {
  setAuditionsFilterData,
  clearAuditionsFilterData,
} = auditionsSlice.actions;
export const {
  getAuditionLoading,
  getAuditions,
  getAuditionsFilterData,
} = auditionsSlice.selectors;

function filterActiveAuditionsByRoleId(auditions, roleId, limit) {
  const activeAuditions = filter(
    auditions,
    audition =>
      audition.projectRoleId === roleId &&
      Audition.getAuditionStatus(audition) === Audition.STATUSES.ACTIVE,
  );
  const orderedAuditions = orderBy(activeAuditions, ['updatedAt'], ['desc']);

  if (limit === 0) {
    return orderedAuditions;
  }

  return slice(orderedAuditions, 0, limit);
}

export const getActiveAuditionsByRoleId = (roleId, limit = 0) =>
  createSelector(
    getAuditions,
    stateAuditions =>
      filterActiveAuditionsByRoleId(stateAuditions, roleId, limit),
  );

export const getAuditionById = auditionId =>
  createSelector(
    getAuditions,
    stateAuditions => find(stateAuditions, { id: auditionId }),
  );

export const getAuditionsByRoleId = roleId =>
  createSelector(
    getAuditions,
    stateAuditions =>
      filter(
        stateAuditions,
        stateAudition => stateAudition.projectRoleId === roleId,
      ),
  );

export const getAppliedAuditionsByTalentId = talentId =>
  createSelector(
    getAuditions,
    getApplications,
    (stateAuditions, stateApplications) =>
      filter(stateAuditions, audition =>
        some(stateApplications, { talentId, auditionId: audition.id }),
      ),
  );
